import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import Loading from '@carbon/react/es/components/Loading/Loading';

import { router } from './router.js';
import { useAuthStore } from './auth/store.js';
import { startAnalytics, track } from './features/analytics/analytics.js';
import { I18nProvider } from './i18n/index.js';
// Polices IBM Plex (Latin), auto-hébergées : Carbon désigne « IBM Plex Sans/Mono » dans ses
// styles, ces @font-face les fournissent. Vite embarque les woff2 (URLs relatives), aucune
// requête vers un CDN — la CSP de production n'autorise que 'self' pour font-src.
//
// Deux familles Sans : **Condensed** (Latin) porte l'UI, **Sans** (large) est gardée en repli de
// pile. Raison : IBM Plex Sans Condensed n'embarque **aucun cyrillique**, alors que Plex Sans, si.
// Le repli par glyphe fait donc rendre le français/anglais en Condensed et l'ukrainien en Plex Sans
// (même famille, couverture complète) ; le sous-ensemble cyrillique n'est téléchargé qu'à l'affichage
// d'ukrainien, sans coût pour les autres langues.
import '@ibm/plex-sans-condensed/css/ibm-plex-sans-condensed-default.css';
import '@ibm/plex-sans/css/ibm-plex-sans-default.css';
import '@ibm/plex-mono/css/ibm-plex-mono-default.css';
import './styles/app.scss';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Ne jamais réessayer sur une erreur d'autorisation : la réponse ne changera pas,
        // et insister ne fait que consommer le quota de rate limiting.
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Amorçage : on tente de restaurer la session (rafraîchissement silencieux depuis le cookie)
 * AVANT de monter le routeur. Sans ce sas, la garde des routes protégées verrait `anonymous` au
 * premier rendu et redirigerait vers la connexion à chaque rechargement, même avec une session
 * valide (story 1.2).
 */
/** Nom de route à faible cardinalité pour l'analytique : l'identifiant **modèle** (`/videos/$videoId`), jamais la valeur. */
function routeName(): string {
  const leaf = router.state.matches.at(-1)?.routeId ?? '';
  const slug = leaf.replace(/^\/+/, '').replace(/[/$]/g, '_');
  return slug || 'home';
}

function AppBoot() {
  const initialized = useAuthStore((state) => state.initialized);
  const bootstrap = useAuthStore((state) => state.bootstrap);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // Analytique d'usage (ADR-0026) : démarrage du vidage par lots + une vue de page à chaque
  // navigation résolue. On n'envoie que le nom de route (modèle), jamais d'identifiant. Un garde
  // « route précédente » évite le doublon entre la vue initiale et la première résolution du routeur.
  useEffect(() => {
    startAnalytics();
    let lastRoute = '';
    const emit = () => {
      const route = routeName();
      if (route === lastRoute) return;
      lastRoute = route;
      track('PAGE_VIEWED', { route });
    };
    emit();
    return router.subscribe('onResolved', emit);
  }, []);

  if (!initialized) {
    return (
      <div className="ddp-boot">
        <Loading withOverlay={false} description="Chargement de la session…" />
      </div>
    );
  }

  return <RouterProvider router={router} />;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <I18nProvider>
      <QueryClientProvider client={queryClient}>
        <AppBoot />
      </QueryClientProvider>
    </I18nProvider>
  </StrictMode>,
);
